import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, User, Upload, Loader2, GraduationCap, Shield, X } from "lucide-react";
import { toast } from "react-toastify";
import AuthNavbar from "../../components/navigations/AuthNavbar";
import clientService from "../../api/ClientService";
import SearchableSelect from "../../components/agents/dashboard/SearchableSelect";
import { ghanaRegions, ghanaLocationsByRegion } from "../../data/ghanaRegionsAndLocations";

export default function EditStudent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const client = location.state?.student || location.state?.client;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalImages, setOriginalImages] = useState({
    studentCardFront: null,
    studentCardBack: null,
  });

  // Normalize client from API (snake_case) or frontend (camelCase)
  const getStudentValue = (client, camelKey, snakeKey) => {
    const c = client || {};
    return c[camelKey] ?? c[snakeKey] ?? "";
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phoneDisplay: "",
    gender: "",
    dob: "",
    region: "",
    location: "",
    customLocation: "",
    schoolName: "",
    studentId: "",
    studentCardIssueDate: "",
    studentCardExpiryDate: "",
    studentCardFront: null,
    studentCardBack: null,
  });

  useEffect(() => {
    if (!client) {
      toast.error("No student data provided");
      navigate("/students-clients");
      return;
    }

    const clientSlug = client.slug ?? client.id;
    if (slug && clientSlug != null && String(clientSlug) !== slug) {
      toast.error("Student slug mismatch");
      navigate("/students-clients");
      return;
    }

    const parseDOB = (dobString) => {
      if (!dobString || dobString.length !== 8) return "";
      const day = dobString.slice(0, 2);
      const month = dobString.slice(2, 4);
      const year = dobString.slice(4, 8);
      return `${year}-${month}-${day}`;
    };

    const parseDateForInput = (dateString) => {
      if (!dateString) return "";
      try {
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      } catch {
        return "";
      }
    };

    const formatPhoneForDisplay = (phone) => {
      if (!phone) return "";
      const p = String(phone).replace(/\D/g, "");
      if (p.startsWith("233")) return `0${p.slice(3)}`;
      return phone;
    };

    const clientRegion = getStudentValue(client, "region", "region") || "";
    const clientLocation = getStudentValue(client, "location", "location") || "";
    let locationValue = clientLocation;
    let customLocationValue = "";

    if (clientRegion && clientLocation) {
      const standardLocations = ghanaLocationsByRegion[clientRegion] || [];
      if (!standardLocations.includes(clientLocation)) {
        locationValue = "Other";
        customLocationValue = clientLocation;
      }
    }

    const rawDob = getStudentValue(client, "dob", "dob");
    const dobForForm = rawDob && rawDob.length === 8 ? parseDOB(rawDob) : parseDateForInput(rawDob);

    setFormData({
      firstName: getStudentValue(client, "firstName", "first_name") || "",
      lastName: getStudentValue(client, "lastName", "last_name") || "",
      email: getStudentValue(client, "email", "email") || "",
      phone: getStudentValue(client, "phone", "phone_number") || "",
      phoneDisplay: formatPhoneForDisplay(getStudentValue(client, "phone", "phone_number")),
      gender: (() => {
        const g = getStudentValue(client, "gender", "gender");
        return g ? g.charAt(0).toUpperCase() + g.slice(1).toLowerCase() : "";
      })(),
      dob: dobForForm,
      region: clientRegion,
      location: locationValue,
      customLocation: customLocationValue,
      schoolName: getStudentValue(client, "schoolName", "school_name") || "",
      studentId: getStudentValue(client, "studentId", "student_id") || getStudentValue(client, "student_school_id", "student_school_id") || "",
      studentCardIssueDate: parseDateForInput(
        getStudentValue(client, "studentCardIssueDate", "student_card_issue_date") || getStudentValue(client, "card_issue_date", "card_issue_date")
      ),
      studentCardExpiryDate: parseDateForInput(
        getStudentValue(client, "studentCardExpiryDate", "student_card_expiry_date") || getStudentValue(client, "card_expiry_date", "card_expiry_date")
      ),
      studentCardFront: client.studentCardFront ?? client.student_card_front ?? client.card_front ?? null,
      studentCardBack: client.studentCardBack ?? client.student_card_back ?? client.card_back ?? null,
    });

    setOriginalImages({
      studentCardFront: client.studentCardFront ?? client.student_card_front ?? client.card_front ?? null,
      studentCardBack: client.studentCardBack ?? client.student_card_back ?? client.card_back ?? null,
    });
  }, [client, navigate, slug]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (value) => {
    const digitsOnly = value.replace(/\D/g, "");
    let formatted = digitsOnly.startsWith("233") ? `0${digitsOnly.slice(3)}` : digitsOnly;
    if (formatted.length > 10) formatted = formatted.slice(0, 10);
    setFormData((prev) => ({
      ...prev,
      phoneDisplay: formatted,
      phone: formatted.startsWith("0") ? `233${formatted.slice(1)}` : formatted,
    }));
  };

  const isPhoneValid = () => {
    if (!formData.phoneDisplay) return false;
    if (formData.phoneDisplay.startsWith("233")) return formData.phoneDisplay.length === 12;
    if (formData.phoneDisplay.startsWith("0")) return formData.phoneDisplay.length === 10;
    return false;
  };

  const handleImageUpload = (field, file) => {
    const reader = new FileReader();
    reader.onload = (e) => setFormData((prev) => ({ ...prev, [field]: e.target.result }));
    reader.readAsDataURL(file);
  };

  const removeImage = (field) => {
    setFormData((prev) => ({ ...prev, [field]: null }));
  };

  const isImageChanged = (current, original) => {
    if (!current) return false;
    if (!original) return current.startsWith("data:image");
    return current.startsWith("data:image") && current !== original;
  };

  const formatDOB = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  };

  const isFormValid = () => {
    const required = ["firstName", "lastName", "email", "phone", "gender", "dob", "region", "location"];
    const filled = required.every((field) => {
      if (field === "location") return formData.location || (formData.location === "Other" && formData.customLocation);
      return formData[field]?.trim();
    });
    return filled && isPhoneValid();
  };

  const createUpdatePayload = () => {
    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone_number: formData.phone,
      location: formData.location === "Other" ? formData.customLocation : formData.location,
      dob: formatDOB(formData.dob),
      region: formData.region,
      gender: formData.gender.toLowerCase(),
    };
    if (formData.schoolName?.trim()) payload.school_name = formData.schoolName.trim();
    if (formData.studentId?.trim()) payload.student_school_id = formData.studentId.trim();
    if (formData.studentCardIssueDate) payload.card_issue_date = formData.studentCardIssueDate;
    if (formData.studentCardExpiryDate) payload.card_expiry_date = formData.studentCardExpiryDate;
    if (formData.studentCardFront) {
      payload.id_card_front = isImageChanged(formData.studentCardFront, originalImages.studentCardFront)
        ? formData.studentCardFront
        : originalImages.studentCardFront;
    }
    if (formData.studentCardBack) {
      payload.id_card_back = isImageChanged(formData.studentCardBack, originalImages.studentCardBack)
        ? formData.studentCardBack
        : originalImages.studentCardBack;
    }
    return payload;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error("Please fill in all required fields");
      return;
    }
    const studentSlug = client?.slug ?? client?.id;
    if (studentSlug == null) {
      toast.error("Student identifier missing");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = createUpdatePayload();
      const response = await clientService.updateStudent(String(studentSlug), payload);

      if (response.status) {
        toast.success(response.message || "Student updated successfully!");
        navigate("/students-clients");
      } else {
        toast.error(response.message || "Failed to update student");
        if (response.validationErrors) console.error("Validation errors:", response.validationErrors);
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Update error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!client) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50">
      <AuthNavbar />

      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 shadow-lg">
        <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/students-clients")}
                className="p-2 rounded-lg transition-all duration-200 text-white/80 hover:text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="flex gap-2 items-center text-3xl font-bold text-white">
                  <GraduationCap className="w-7 h-7" />
                  Edit Student
                </h1>
                <p className="mt-1 text-sm text-emerald-100">Update student information</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-8 bg-white rounded-2xl border-2 border-gray-200 shadow-xl"
        >
          {/* Personal Information */}
          <div className="mb-8">
            <h2 className="flex items-center mb-6 text-xl font-bold text-gray-900">
              <User className="mr-2 w-5 h-5 text-emerald-600" />
              Personal Information
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">First Name *</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  className="px-4 py-3 w-full rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Last Name *</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  className="px-4 py-3 w-full rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Guardian Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="px-4 py-3 w-full rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Guardian Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phoneDisplay}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="0XXXXXXXXX"
                  className="px-4 py-3 w-full rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div className="space-y-2">
                <label className="flex gap-2 items-center text-base font-semibold text-gray-700">Gender *</label>
                <div className="flex gap-3">
                  {["Male", "Female"].map((option) => (
                    <motion.label
                      key={option}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`flex items-center gap-3 px-4 py-3 flex-1 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                        formData.gender.toLowerCase() === option.toLowerCase()
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                      onClick={() => handleInputChange("gender", option)}
                    >
                      <div
                        className={`relative w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                          formData.gender.toLowerCase() === option.toLowerCase()
                            ? "border-emerald-500 bg-emerald-500"
                            : "border-gray-300"
                        }`}
                      >
                        {formData.gender.toLowerCase() === option.toLowerCase() && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute inset-0.5 rounded-full bg-white"
                          />
                        )}
                      </div>
                      <span
                        className={`text-sm font-medium ${
                          formData.gender.toLowerCase() === option.toLowerCase()
                            ? "text-emerald-700"
                            : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </motion.label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Date of Birth *</label>
                <input
                  type="date"
                  value={formData.dob}
                  onChange={(e) => handleInputChange("dob", e.target.value)}
                  className="px-4 py-3 w-full rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Region *</label>
                <SearchableSelect
                  options={ghanaRegions}
                  value={formData.region}
                  onChange={(value) => {
                    handleInputChange("region", value);
                    handleInputChange("location", "");
                    handleInputChange("customLocation", "");
                  }}
                  placeholder="Select Region"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Location *</label>
                {formData.region ? (
                  <>
                    <SearchableSelect
                      options={ghanaLocationsByRegion[formData.region] || []}
                      value={formData.location}
                      onChange={(value) => {
                        if (value === "Other") handleInputChange("location", "Other");
                        else {
                          handleInputChange("location", value);
                          handleInputChange("customLocation", "");
                        }
                      }}
                      placeholder="Select Location"
                      showOther={true}
                      otherPlaceholder="Enter custom location"
                      label="location"
                    />
                    {formData.location === "Other" && (
                      <input
                        type="text"
                        value={formData.customLocation}
                        onChange={(e) => handleInputChange("customLocation", e.target.value)}
                        className="px-4 py-3 mt-2 w-full rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Enter custom location"
                      />
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange("location", e.target.value)}
                    placeholder="Enter Location"
                    className="px-4 py-3 w-full rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Student Information */}
          <div className="mb-8">
            <h2 className="flex items-center mb-6 text-xl font-bold text-gray-900">
              <GraduationCap className="mr-2 w-5 h-5 text-blue-600" />
              Student Information
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">School Name</label>
                <input
                  type="text"
                  value={formData.schoolName}
                  onChange={(e) => handleInputChange("schoolName", e.target.value)}
                  placeholder="e.g. University of Ghana"
                  className="px-4 py-3 w-full rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Student ID</label>
                <input
                  type="text"
                  value={formData.studentId}
                  onChange={(e) => handleInputChange("studentId", e.target.value)}
                  placeholder="e.g. UG-2024-001"
                  className="px-4 py-3 w-full rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Student Card Issue Date</label>
                <input
                  type="date"
                  value={formData.studentCardIssueDate}
                  onChange={(e) => handleInputChange("studentCardIssueDate", e.target.value)}
                  className="px-4 py-3 w-full rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">Student Card Expiry Date</label>
                <input
                  type="date"
                  value={formData.studentCardExpiryDate}
                  onChange={(e) => handleInputChange("studentCardExpiryDate", e.target.value)}
                  className="px-4 py-3 w-full rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block flex gap-2 items-center mb-4 text-sm font-medium text-gray-700">
                <Shield className="w-4 h-4" />
                Student Card Images
              </label>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="block mb-3 text-sm font-medium text-gray-700">Front</label>
                  {formData.studentCardFront ? (
                    <div className="relative">
                      <img
                        src={formData.studentCardFront}
                        alt="Student card front"
                        className="object-cover w-full h-48 rounded-lg border-2 border-gray-200"
                      />
                      <button
                        onClick={() => removeImage("studentCardFront")}
                        className="flex absolute -top-2 -right-2 justify-center items-center w-8 h-8 text-white bg-red-500 rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <label className="absolute bottom-2 left-2 block px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg cursor-pointer hover:bg-emerald-700">
                        <Upload className="inline mr-1 w-3 h-3" />
                        Change
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => e.target.files[0] && handleImageUpload("studentCardFront", e.target.files[0])}
                          className="hidden"
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="block w-full h-48 rounded-lg border-2 border-gray-300 border-dashed cursor-pointer hover:border-emerald-400">
                      <div className="flex flex-col justify-center items-center h-full text-gray-600">
                        <Upload className="mb-2 w-8 h-8" />
                        <span className="text-sm">Upload front image</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => e.target.files[0] && handleImageUpload("studentCardFront", e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
                <div>
                  <label className="block mb-3 text-sm font-medium text-gray-700">Back</label>
                  {formData.studentCardBack ? (
                    <div className="relative">
                      <img
                        src={formData.studentCardBack}
                        alt="Student card back"
                        className="object-cover w-full h-48 rounded-lg border-2 border-gray-200"
                      />
                      <button
                        onClick={() => removeImage("studentCardBack")}
                        className="flex absolute -top-2 -right-2 justify-center items-center w-8 h-8 text-white bg-red-500 rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <label className="absolute bottom-2 left-2 block px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg cursor-pointer hover:bg-emerald-700">
                        <Upload className="inline mr-1 w-3 h-3" />
                        Change
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => e.target.files[0] && handleImageUpload("studentCardBack", e.target.files[0])}
                          className="hidden"
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="block w-full h-48 rounded-lg border-2 border-gray-300 border-dashed cursor-pointer hover:border-emerald-400">
                      <div className="flex flex-col justify-center items-center h-full text-gray-600">
                        <Upload className="mb-2 w-8 h-8" />
                        <span className="text-sm">Upload back image</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => e.target.files[0] && handleImageUpload("studentCardBack", e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-4 justify-end pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate("/students-clients")}
              className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white rounded-xl border-2 border-gray-200 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex gap-2 items-center px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl transition-all hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Student"
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
